import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiCopy, FiShare2, FiCheck } from 'react-icons/fi';
import { trackEvent } from '../../services/analytics';
import { groupInviteUrl } from '../../utils/host';

// Share a group's invite link (e.g. https://teesync.co.uk/join/{invite_code}).
// The link opens a public landing page that names the group and points a
// recipient without the app at the App Store, so the code no longer has to be
// read out separately.
//
// Structural sibling of ShareRoundModal, minus the link-sharing toggle: groups
// have no kill switch, and the invite code is rotated from group settings
// instead. navigator.share is reused rather than adding @capacitor/share -
// WKWebView supports it and ShareRoundModal already ships on iOS this way.

const ShareGroupInviteModal = ({ isOpen, onClose, inviteCode, groupName, ownerName, surface = 'group_details' }) => {
  const [copied, setCopied] = useState(false);

  if (!inviteCode) return null;

  const link = groupInviteUrl(inviteCode);
  const message = ownerName
    ? `${ownerName} added you to ${groupName || 'a group'} on TeeSync - tap to join: ${link}`
    : `Join ${groupName || 'our group'} on TeeSync: ${link}`;

  const trackShared = (method) =>
    trackEvent('group_invite_link_shared', { invite_code: inviteCode, method, surface });

  const handleNativeShare = async () => {
    try {
      // Message text carries the URL rather than the separate `url` field:
      // iOS Messages renders {text, url} as two lines.
      await navigator.share({ text: message });
      trackShared('share_sheet');
    } catch {
      // user dismissed the sheet - not an error
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      trackShared('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable - leave button state unchanged
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full sm:max-w-md bg-card rounded-t-2xl sm:rounded-2xl border border-border-subtle p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-text-primary">Share invite link</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-9 h-9 rounded-lg bg-surface flex items-center justify-center"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-4">
              The link names the group and gets them the app if they do not have it yet.
            </p>

            <div className="rounded-lg bg-subtle border border-border-subtle px-3 py-2.5 mb-4 font-mono text-sm text-text-primary break-all">
              {link}
            </div>

            <div className="space-y-2">
              {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
                <button
                  type="button"
                  onClick={handleNativeShare}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 font-semibold text-white"
                >
                  <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  Share
                </button>
              )}
              <button
                type="button"
                onClick={handleCopy}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-border-subtle bg-surface px-4 py-3 font-semibold text-text-primary"
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
                {copied ? 'Copied' : 'Copy invite message'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareGroupInviteModal;
